import DataObject from "@mui/icons-material/DataObject";
import {
	Box,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Stack,
	Typography
} from "@mui/material";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";

import apiRoutes from "@/constants/api-routes";
import {
	getDefaultVariableValue,
	VARIABLE_DATA_TYPE_OPTIONS,
	type VariableDataTypeOption,
} from "@/constants/rule-variables";
import useAuthHeader from "@/hooks/useAuthHeader";

import { CoAppActionButton, CoAppCancelTextButton, CoAppDialog } from "../../global/styled/global.styled";
import type { Option } from "../constants";
import { DataTypeField } from "../fields/DataTypeField";
import type {
	DatabaseTableOptionV2,
	ResourceCategoryV2,
	VariableGroupV2,
	VisibleGroupKey,
} from "../fields/form-entity-select";
import FormEntitySelect from "../fields/form-entity-select";
import type { SimpleVariableFormState } from "../forms/SimpleVariableForm";
import { CanvasFormTextField } from "../forms/styled/CanvasFormTextField.styled";
import type { RuleVariable } from "../hooks/useRuleVariables";
import { inferValueCategoryFromEntityFields, isDecisionValueEntityRow } from "../utils/decision-statement-value-utils";
import { buildFormulaVariableRef, buildScreenFieldRef, selectedFieldMetaFromFormulaVariable, selectedFieldMetaFromScreenField } from "../utils/data-field-type-utils";
import { isVariableNameTakenInRule } from "../utils/variable-naming-utils";

const resolveDataTypeValue = (id: number | null) =>
	VARIABLE_DATA_TYPE_OPTIONS.find((opt) => opt.id === id)?.value ?? null;

const EMPTY_OPTIONS: Option[] = [];
const EMPTY_VARIABLE_GROUPS: VariableGroupV2[] = [];

/** Mirrors `SimpleVariableFormState` plus internal `value` used when persisting the variable. */
export type SimpleVariableModalState = SimpleVariableFormState & {
	value?: string;
};

export interface SimpleVariableModalProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: SimpleVariableModalState & { dataTypeId: number }) => void;
	isSaving?: boolean;
	existingVariables?: RuleVariable[];
	editingVariableId?: string | null;
	platformId?: string | number;
	resourceVariableGroups?: VariableGroupV2[];
	formulaVariableOptions?: Option[];
	resourceScreenFields?: Option[];
	globalConstantOptions?: Option[];
	ruleSimpleVariables?: RuleVariable[];
	ruleFormulaVariables?: RuleVariable[];
	ruleDatabaseSingleRowVariables?: RuleVariable[];
	ruleDatabaseMultiRowVariables?: RuleVariable[];
}

const emptyValueEntityFields = (): Pick<
	SimpleVariableFormState,
	| "valueLiteral"
	| "valueDatabaseTableFieldId"
	| "valueScreenFieldId"
	| "valueDatabaseTableFieldName"
	| "valueScreenFieldName"
	| "valueScreenField"
	| "valueSimpleVariableName"
	| "valueSimpleVariableId"
	| "valueFormulaVariableId"
	| "valueFormulaVariableName"
	| "valueFormulaVariable"
	| "valueDatabaseSingleRowVariableId"
	| "valueDatabaseSingleRowVariableName"
	| "valueGlobalConstant"
	| "valueGlobalConstantName"
> => ({
	valueLiteral: undefined,
	valueDatabaseTableFieldId: undefined,
	valueScreenFieldId: undefined,
	valueDatabaseTableFieldName: undefined,
	valueScreenFieldName: undefined,
	valueScreenField: undefined,
	valueSimpleVariableName: undefined,
	valueSimpleVariableId: undefined,
	valueFormulaVariableId: undefined,
	valueFormulaVariableName: undefined,
	valueFormulaVariable: undefined,
	valueDatabaseSingleRowVariableId: undefined,
	valueDatabaseSingleRowVariableName: undefined,
	valueGlobalConstant: undefined,
	valueGlobalConstantName: undefined,
});

const SimpleVariableModal = (props: SimpleVariableModalProps) => {
	const {
		open,
		onClose,
		onSave,
		isSaving = false,
		existingVariables = [],
		editingVariableId = null,
		platformId,
		resourceVariableGroups = EMPTY_VARIABLE_GROUPS,
		formulaVariableOptions = EMPTY_OPTIONS,
		resourceScreenFields = EMPTY_OPTIONS,
		globalConstantOptions = EMPTY_OPTIONS,
		ruleSimpleVariables = [],
		ruleFormulaVariables = [],
		ruleDatabaseSingleRowVariables = [],
		ruleDatabaseMultiRowVariables = [],
	} = props;
	const authHeader = useAuthHeader();

	const resourceVariableGroupsForValue = useMemo((): VariableGroupV2[] => {
		if (!editingVariableId) {
			return resourceVariableGroups;
		}
		return resourceVariableGroups.map((group) => ({
			...group,
			options: group.options.filter((opt) => {
				const match = ruleSimpleVariables.find((v) => v.name === opt.value);
				return !match || match.id !== editingVariableId;
			}),
		}));
	}, [editingVariableId, resourceVariableGroups, ruleSimpleVariables]);

	const buildInitialState = (): SimpleVariableModalState => {
		const initialId = 1;
		const initialValue = resolveDataTypeValue(initialId);
		return {
			name: "",
			dataTypeId: initialId,
			value: initialValue ? getDefaultVariableValue(initialValue) : "",
			valueLiteral: undefined,
			valueCategory: undefined,
		};
	};

	const initialFormState = useMemo(buildInitialState, []);
	const [formState, setFormState] = useState<SimpleVariableModalState>(initialFormState);
	const [emptyNameError, setEmptyNameError] = useState(false);
	const [resolvedDataTypeOption, setResolvedDataTypeOption] = useState<VariableDataTypeOption | null>(null);
	const resolvedDataTypeRef = useRef<VariableDataTypeOption | null>(null);
	resolvedDataTypeRef.current = resolvedDataTypeOption;

	const [fetchedScreenFields, setFetchedScreenFields] = useState<Option[]>([]);
	const [ruleVariableSingleRowTables, setRuleVariableSingleRowTables] = useState<DatabaseTableOptionV2[]>([]);
	const [ruleVariableMultiRowTables, setRuleVariableMultiRowTables] = useState<DatabaseTableOptionV2[]>([]);

	useEffect(() => {
		if (!open || !platformId) return;

		let cancelled = false;

		const fetchResourceOptions = async () => {
			try {
				const tablesResponse = await axios.get(apiRoutes.getTableOptions(platformId), { headers: authHeader });
				if (cancelled) return;
				const rawGroups = tablesResponse.data?.tableGroups ?? [];

				const screenTables: { id: number; name: string }[] = [];

				for (const group of rawGroups) {
					if (group.screenTables) screenTables.push(...group.screenTables);
				}

				const screenFieldResults = await Promise.all(
					screenTables.map(async (table) => {
						try {
							const res = await axios.get(apiRoutes.getScreenTableFields(table.id), { headers: authHeader });
							return (res.data ?? []).map((field: { id: number; name: string }) => ({
								label: `${table.name}.${field.name}`,
								value: String(field.id),
							}));
						} catch {
							return [];
						}
					}),
				);
				if (cancelled) return;
				setFetchedScreenFields(screenFieldResults.flat());
			} catch (error) {
				console.error("Error fetching resource options:", error);
			}
		};

		fetchResourceOptions();
		return () => {
			cancelled = true;
		};
	}, [open, platformId, authHeader]);

	useEffect(() => {
		if (!open || !platformId) return;

		let cancelled = false;

		const fetchRuleDatabaseVariableFields = async () => {
			const mapVars = async (vars: RuleVariable[]) =>
				Promise.all(
					vars.map(async (v) => {
						const tableId = v.source;
						if (!tableId) {
							return { label: v.name, value: v.id, fields: [] as Option[] };
						}
						try {
							const res = await axios.get(apiRoutes.getDatabaseTableFields(tableId), { headers: authHeader });
							const fields: Option[] = (res.data ?? []).map((field: { id: number; name: string }) => ({
								label: field.name,
								value: String(field.id),
							}));
							return { label: v.name, value: v.id, fields };
						} catch {
							return { label: v.name, value: v.id, fields: [] as Option[] };
						}
					}),
				);

			try {
				const [single, multi] = await Promise.all([
					mapVars(ruleDatabaseSingleRowVariables),
					mapVars(ruleDatabaseMultiRowVariables),
				]);
				if (cancelled) return;
				setRuleVariableSingleRowTables(single);
				setRuleVariableMultiRowTables(multi);
			} catch (error) {
				console.error("Error fetching database variable fields:", error);
			}
		};

		fetchRuleDatabaseVariableFields();
		return () => {
			cancelled = true;
		};
	}, [open, platformId, ruleDatabaseSingleRowVariables, ruleDatabaseMultiRowVariables, authHeader]);

	useEffect(() => {
		if (open) {
			setFormState(buildInitialState());
			setEmptyNameError(false);
			setResolvedDataTypeOption(null);
		}
	}, [open]);

	const duplicateNameMessage = useMemo(() => {
		const trimmed = formState.name.trim();
		if (!trimmed) {
			return null;
		}
		return isVariableNameTakenInRule(trimmed, existingVariables, editingVariableId)
			? "That name is already in use"
			: null;
	}, [editingVariableId, existingVariables, formState.name]);

	const nameHelperText = duplicateNameMessage ?? (emptyNameError ? "Enter a variable name" : null);
	const hasNameError = Boolean(duplicateNameMessage || emptyNameError);

	const mergedScreenFields = useMemo(
		() => (resourceScreenFields.length ? resourceScreenFields : fetchedScreenFields),
		[resourceScreenFields, fetchedScreenFields],
	);

	const resourceVisibleGroups = useMemo((): VisibleGroupKey[] => {
		const keys: VisibleGroupKey[] = [];
		if (mergedScreenFields.length) keys.push("screenFields");
		const hasSimpleVariables = resourceVariableGroupsForValue.some((g) => g.options.length > 0);
		if (hasSimpleVariables) keys.push("simpleVariable");
		if (formulaVariableOptions.length) keys.push("formulaVariable");
		if (ruleVariableSingleRowTables.length) keys.push("singleRowDatabaseVariables");
		if (ruleVariableMultiRowTables.length) keys.push("multiRowDatabaseVariables");
		return keys;
	}, [
		mergedScreenFields,
		resourceVariableGroupsForValue,
		formulaVariableOptions.length,
		ruleVariableSingleRowTables.length,
		ruleVariableMultiRowTables.length,
	]);

	const valueFieldVisibleGroups = useMemo((): VisibleGroupKey[] => {
		const keys = [...resourceVisibleGroups];
		if (globalConstantOptions.length) keys.push("globalConstants");
		return keys;
	}, [resourceVisibleGroups, globalConstantOptions.length]);

	const handleDataTypeChange = (id: number | null, selectedOption: VariableDataTypeOption | null = null) => {
		setResolvedDataTypeOption(selectedOption);
		setFormState((previous) => {
			const prevType =
				resolvedDataTypeRef.current?.value ?? resolveDataTypeValue(previous.dataTypeId);
			const newType = selectedOption?.value ?? resolveDataTypeValue(id);
			return {
				...previous,
				dataTypeId: id,
				...emptyValueEntityFields(),
				value: newType
					? newType === prevType
						? previous.valueLiteral
						: getDefaultVariableValue(newType)
					: "",
			};
		});
	};

	const handleValueEntityChange = (
		val: string,
		category: ResourceCategoryV2,
		label: string,
		dataType?: string,
	) => {
		const emptyEntity = emptyValueEntityFields();
		const trimmed = val.trim();
		if (!trimmed || category === "literal") {
			setFormState((previous) => ({
				...previous,
				...emptyEntity,
				value: trimmed,
				valueLiteral: trimmed || undefined,
				valueCategory: category,
			}));
			return;
		}
		if (
			category === "singleRowDbVariableField" ||
			category === "multiRowDbVariableField"
		) {
			setFormState((previous) => ({
				...previous,
				...emptyEntity,
				value: val,
				valueLiteral: undefined,
				valueDatabaseTableFieldId: val,
				valueDatabaseTableFieldName: label,
				valueCategory: category,
			}));
			return;
		}
		if (category === "screenTableField" || category === "screenField") {
			const screenRef = buildScreenFieldRef(label, dataType);
			setFormState((previous) => ({
				...previous,
				...emptyEntity,
				value: val,
				valueLiteral: undefined,
				valueScreenFieldId: val,
				valueScreenFieldName: label,
				valueScreenField: screenRef,
				valueCategory: category,
			}));
			return;
		}
		if (category === "simpleVariable") {
			const id = ruleSimpleVariables.find((v) => v.name === val)?.id;
			setFormState((previous) => ({
				...previous,
				...emptyEntity,
				value: val,
				valueLiteral: undefined,
				valueSimpleVariableId: id,
				valueSimpleVariableName: val,
				valueCategory: category,
			}));
			return;
		}
		if (category === "formulaVariable") {
			const id = ruleFormulaVariables.find((v) => v.name === val)?.id;
			setFormState((previous) => ({
				...previous,
				...emptyEntity,
				value: val,
				valueLiteral: undefined,
				valueFormulaVariableId: id,
				valueFormulaVariableName: val,
				valueFormulaVariable: buildFormulaVariableRef(
					val,
					dataType,
					previous.valueFormulaVariable,
					id,
				),
				valueCategory: category,
			}));
			return;
		}
		if (category === "globalConstant") {
			setFormState((previous) => ({
				...previous,
				...emptyEntity,
				value: val,
				valueLiteral: undefined,
				valueGlobalConstant: val,
				valueGlobalConstantName: label,
				valueCategory: category,
			}));
		}
	};

	const valueSelectControlValue =
		formState.valueGlobalConstant ||
		formState.valueDatabaseTableFieldId ||
		formState.valueScreenFieldId ||
		formState.valueDatabaseSingleRowVariableId ||
		formState.valueSimpleVariableName ||
		formState.valueFormulaVariableName ||
		formState.valueLiteral ||
		"";

	const isValueMissing = valueSelectControlValue.trim() === "";

	const isSaveDisabled =
		!formState.name.trim() ||
		Boolean(duplicateNameMessage) ||
		isValueMissing ||
		isSaving;

	const handleSave = () => {
		const trimmedName = formState.name.trim();
		if (!trimmedName) {
			setEmptyNameError(true);
			return;
		}

		if (isVariableNameTakenInRule(trimmedName, existingVariables, editingVariableId)) {
			return;
		}

		if (formState.dataTypeId == null) {
			return;
		}

		setEmptyNameError(false);
		const trimmed = formState.valueLiteral?.trim();
		const entity = isDecisionValueEntityRow(formState);

		onSave({
			...formState,
			name: trimmedName,
			dataTypeId: formState.dataTypeId,
			valueLiteral: entity ? undefined : trimmed || undefined,
		});
	};

	return (
		<CoAppDialog
			open={open}
			fullWidth
			maxWidth="md"
			slotProps={{
				paper: {
					sx: {
						borderRadius: 3,
						padding: 2,
						backgroundColor: "#FFFFFF"
					}
				}
			}}
		>
			<DialogTitle sx={{ pb: 0 }}>
				<Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
					<span style={{ color: "#D48200", display: "flex", alignItems: "center" }}>
						<DataObject />
					</span>
					<Typography variant="h6">Simple Variable</Typography>
				</Stack>
			</DialogTitle>

			<Divider sx={{ my: 2 }} />

			<DialogContent sx={{ pt: 1 }}>
				<Stack spacing={3}>
					<Stack
						direction={{ xs: "column", md: "row" }}
						spacing={2}
						sx={{ minWidth: 0, width: "100%" }}
					>
						<Box sx={{ flex: "1 1 0", minWidth: 0, width: { xs: "100%", md: "auto" } }}>
							<CanvasFormTextField
								fullWidth
								label="Variable Name"
								value={formState.name}
								onChange={(event) => {
									setEmptyNameError(false);
									setFormState((previous) => ({ ...previous, name: event.target.value }));
								}}
								error={hasNameError}
								helperText={nameHelperText}
								width="100%"
							/>
						</Box>
						<Box
							sx={{
								flex: "0 0 auto",
								minWidth: 0,
								width: { xs: "100%", md: "auto" },
								maxWidth: { md: 160 },
								"& .MuiFormControl-root": {
									minWidth: 0,
									width: "100%",
								},
								"& .MuiOutlinedInput-root": {
									minWidth: 0,
									overflow: "hidden",
								},
								"& .MuiSelect-select": {
									whiteSpace: "nowrap",
									textOverflow: "ellipsis",
									overflow: "hidden",
									display: "block",
									minWidth: 0,
									maxWidth: "100%",
								},
							}}
						>
							<DataTypeField
								value={formState.dataTypeId}
								onChange={handleDataTypeChange}
							/>
						</Box>
						<Box sx={{ flex: "1 1 0", minWidth: 0, width: { xs: "100%", md: "auto" } }}>
							<FormEntitySelect
								label="Value"
								isValueField
								value={valueSelectControlValue}
								valueCategory={inferValueCategoryFromEntityFields(formState)}
								onChange={handleValueEntityChange}
								visibleGroups={valueFieldVisibleGroups}
								variableGroups={resourceVariableGroupsForValue}
								formulaVariables={formulaVariableOptions}
								databaseSingleRowVariableOptions={EMPTY_OPTIONS}
								singleRowDbVariables={ruleVariableSingleRowTables}
								multiRowDbVariables={ruleVariableMultiRowTables}
								screenFields={mergedScreenFields}
								globalConstants={globalConstantOptions}
								selectedFieldMeta={
									formState.valueScreenFieldId
										? selectedFieldMetaFromScreenField(
												formState.valueScreenField,
												formState.valueScreenFieldId,
											)
										: formState.valueFormulaVariableId || formState.valueFormulaVariableName
											? selectedFieldMetaFromFormulaVariable(
													formState.valueFormulaVariable ??
														buildFormulaVariableRef(
															formState.valueFormulaVariableName,
															undefined,
															undefined,
															formState.valueFormulaVariableId,
														),
												)
											: undefined
								}
							/>
						</Box>
					</Stack>
				</Stack>
			</DialogContent>

			<DialogActions sx={{ px: 1, pb: 2, pt: 1 }}>
				<CoAppActionButton
					onClick={handleSave}
					disabled={isSaveDisabled}
					sx={{ backgroundColor: "#02D5AB", color: "#FFFFFF", width: "50%" }}
				>
					{isSaving ? "Saving..." : "Save Simple Variable"}
				</CoAppActionButton>
				<CoAppCancelTextButton onClick={onClose} disabled={isSaving} sx={{ width: "50%" }}>
					Cancel
				</CoAppCancelTextButton>
			</DialogActions>
		</CoAppDialog>
	);
};

export default SimpleVariableModal;
